// Maison Jollie — sale countdown on the product page: <mj-countdown>.
// The stock, delivery, cutoff and free shipping messages are in mj-conversion.js.
// The countdown end is computed in the store's timezone (data-timezone, default Europe/Paris),
// so the visitor's own clock or location doesn't change it.

/** Date parts (year, month, day, hour, minute, second, weekday 0-6) of a timestamp in a timezone. */
function zonedParts(timestamp, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(timestamp));
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
  };
}

/** Timestamp of a wall-clock time (year, month, day, hour, minute) in a timezone. */
function zonedTimestamp(year, month, day, hour, minute, timeZone) {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  const offset = (ts) => {
    const p = zonedParts(ts, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ts;
  };
  let timestamp = asUtc - offset(asUtc);
  const secondOffset = offset(timestamp);
  if (asUtc - secondOffset !== timestamp) timestamp = asUtc - secondOffset;
  return timestamp;
}

const pad = (n) => String(n).padStart(2, '0');

/* ---------- Sale countdown ---------- */
class MjCountdown extends HTMLElement {
  connectedCallback() {
    const [year, month, day] = (this.dataset.endDate || '').split('-').map(Number);
    const [hour, minute] = (this.dataset.endTime || '23:59').split(':').map(Number);
    if (!year || !month || !day) return this.remove();
    this.end = zonedTimestamp(year, month, day, hour || 0, minute || 0, this.dataset.timezone || 'Europe/Paris');
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const remaining = Math.floor((this.end - Date.now()) / 1000);
    if (remaining <= 0) {
      clearInterval(this.timer);
      return this.remove();
    }
    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    const output = this.querySelector('[data-mj-countdown-time]');
    if (output) {
      output.textContent = `${days > 0 ? `${days} j ` : ''}${pad(hours)} h ${pad(minutes)} min ${pad(seconds)} s`;
    }
    this.hidden = false;
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('mj-countdown', MjCountdown);
